import * as _ from "lodash";
import { compareIgnoreCase } from "@ane/core/lib/utils/CommonStringUtils";
import "../models/index.js";
import { gatingUtils } from "./GatingUtils.js";
import { ExperimentStepStates, ExperimentStates } from "../models/FeatureDataModel.js";
const getFrontierStageIndexFromStates = (states) => {
  let frontierStateIndex = states.length - 1;
  for (let i = 0; i < states.length; ++i) {
    let s = states[i].toLocaleLowerCase();
    if (s !== ExperimentStepStates.COMPLETED && s !== ExperimentStepStates.STOP_REQUESTED && s !== ExperimentStates.STOPPING && s !== ExperimentStates.MIGRATING && s !== ExperimentStates.MIGRATIONSUCCEEDED && s !== ExperimentStates.MIGRATIONFAILED) {
      if (i + 1 < states.length) {
        const next = states[i + 1].toLocaleLowerCase();
        if (next !== ExperimentStepStates.NEW) {
          continue;
        }
      }
      frontierStateIndex = i;
      break;
    }
  }
  return frontierStateIndex;
};
const getFrontierStageIndex = (progressionStage) => {
  let states = progressionStage.stages.map((t) => t.state.toLocaleLowerCase());
  return getFrontierStageIndexFromStates(states);
};
const getAggregatedProgressionState = (targets) => {
  let states = targets.map((t) => t.state.State);
  return getAggregatedState(states);
};
const getAggregatedStageState = (phases) => {
  let states = phases.map((t) => t.State);
  return getAggregatedState(states);
};
const getAggregatedState = (states) => {
  states = states.map((s) => s.toLocaleLowerCase());
  let activeStates = _.filter(
    states,
    (p) => p !== ExperimentStepStates.COMPLETED && p !== ExperimentStepStates.NEW && p !== ExperimentStepStates.MIGRATING && p !== ExperimentStepStates.MIGRATIONFAILED && p !== ExperimentStepStates.MIGRATIONSUCCEEDED
  );
  let hasActiveStates = activeStates.length > 0;
  let lastActiveState = hasActiveStates ? _.last(activeStates) : null;
  const frontierStateIndex = getFrontierStageIndexFromStates(states);
  let upcomingState = _.find(states, (p) => p === ExperimentStepStates.NEW);
  let state = "";
  if (_.some(
    states,
    (p) => p === ExperimentStepStates.RUNNING || p === ExperimentStepStates.RUNNING_WITH_ISSUES || p === ExperimentStates.RUNNING
  )) {
    state = ExperimentStates.RUNNING;
  } else if (hasActiveStates && (lastActiveState === ExperimentStepStates.START_REQUESTED || lastActiveState === ExperimentStates.STARTING)) {
    state = ExperimentStates.STARTING;
  } else if (hasActiveStates && (lastActiveState === ExperimentStepStates.STOP_REQUESTED || lastActiveState === ExperimentStates.STOPPING)) {
    state = ExperimentStates.STOPPING;
  } else if (states.length > 0 && _.some(states, (p) => p === ExperimentStepStates.COMPLETED || p === ExperimentStates.COMPLETED)) {
    state = ExperimentStates.COMPLETED;
  } else if (states.length === 0 || !hasActiveStates && !!upcomingState) {
    state = ExperimentStates.NEW;
  } else if (states.length === 1 && states[0] === ExperimentStepStates.MIGRATING) {
    state = ExperimentStates.MIGRATING;
  } else if (states.length === 1 && states[0] === ExperimentStepStates.MIGRATIONSUCCEEDED) {
    state = ExperimentStates.MIGRATIONSUCCEEDED;
  } else if (states.length === 1 && states[0] === ExperimentStepStates.MIGRATIONFAILED) {
    state = ExperimentStates.MIGRATIONFAILED;
  } else {
    state = ExperimentStates.UNKNOWN;
  }
  let friendlyLabel = getFriendlyLabel(state);
  return {
    State: state,
    FriendlyLabel: friendlyLabel,
    FrontierStageIndex: frontierStateIndex,
    FrontierIsLastStage: frontierStateIndex === states.length - 1 || states.length === 0
  };
};
const isProgressionRunningOrStopping = (progression) => {
  return progression.state.State === ExperimentStates.RUNNING || progression.state.State === ExperimentStates.STARTING || progression.state.State === ExperimentStates.STOPPING;
};
const isProgressionNotStarted = (progression) => {
  return progression.state.State === ExperimentStates.NEW && progression.allProgressionStages.every(
    (s) => s.state.State.toLowerCase() === ExperimentStates.NEW && s.stages.every((st) => st.state.toLowerCase() === ExperimentStates.NEW)
  );
};
const isProgressionRunning = (progression) => {
  return progression.state.State === ExperimentStates.RUNNING || progression.state.State === ExperimentStates.STARTING;
};
const isProgressionStageStateNotRunning = (progressionStage) => {
  return progressionStage.state.State === ExperimentStates.COMPLETED || progressionStage.state.State === ExperimentStates.STOPPING || progressionStage.state.State === ExperimentStates.NEW;
};
const showProgressionOnLoad = (progressions, progression, index) => {
  return !progressions[index + 1] || !isProgressionNotRunning(progression);
};
const isProgressionStageNotRunning = (progressionStage) => {
  return isProgressionStageStateNotRunning(progressionStage) && progressionStage.stages.some((t) => ExperimentStepStates.stepNotRunning(t.state));
};
const isProgressionNotRunning = (progression) => {
  return isProgressionStateNotRunning(progression) && progression.filteredProgressionStages.some((t) => isProgressionStageNotRunning(t));
};
const isProgressionStateNotRunning = (progression) => {
  return progression.state.State === ExperimentStates.COMPLETED || progression.state.State === ExperimentStates.STOPPING || progression.state.State === ExperimentStates.NEW;
};
const isProgressionStageRunningInLastStep = (progressionStage) => {
  const lastStep = progressionStage.stages[progressionStage.stages.length - 1];
  return ExperimentStepStates.isStartingOrRunning(lastStep.state) || ExperimentStepStates.isStoppingOrCompleted(lastStep.state);
};
const isProgressionStageRunningAt100Percent = (progression) => {
  const runningStages = progression.filteredProgressionStages.filter((s) => !isProgressionStageNotRunning(s));
  return runningStages.length != 0 && !runningStages.find((stage) => {
    const lastStep = stage.stages[stage.stages.length - 1];
    const trafficStepValues = Object.values(lastStep.flightTrafficMap);
    return !compareIgnoreCase(lastStep.state, ExperimentStepStates.RUNNING) || Math.max(...trafficStepValues) != 100;
  });
};
const getFriendlyLabel = (state) => {
  state = state.toLowerCase();
  if (!state) {
    return "unknown";
  }
  switch (state) {
    case ExperimentStates.COMPLETED:
    case ExperimentStepStates.COMPLETED:
      return "Stopped";
    case ExperimentStates.STARTING:
    case ExperimentStepStates.START_REQUESTED:
      return "Starting";
    case ExperimentStates.STOPPING:
    case ExperimentStepStates.STOP_REQUESTED:
      return "Stopping";
    case ExperimentStates.RUNNING:
    case ExperimentStepStates.RUNNING:
    case ExperimentStepStates.RUNNING_WITH_ISSUES:
      return "Running";
    case ExperimentStates.NEW:
    case ExperimentStepStates.NEW:
      return "Not Started";
    case ExperimentStates.MIGRATING:
    case ExperimentStepStates.MIGRATING:
    case ExperimentStates.MIGRATIONFAILED:
    case ExperimentStepStates.MIGRATIONFAILED:
      return "Migrating";
    case ExperimentStates.MIGRATIONSUCCEEDED:
    case ExperimentStepStates.MIGRATIONSUCCEEDED:
      return "Migrated";
    default:
      return state;
  }
};
class ProgressionStageStateUtils {
  canStart(stage) {
    return this.checkState(stage, ExperimentStates.NEW);
  }
  isRunning(stage) {
    return this.checkState(stage, ExperimentStates.RUNNING);
  }
  canDelete(stage) {
    return this.checkState(stage, ExperimentStates.NEW) || this.checkState(stage, ExperimentStates.COMPLETED);
  }
  canStop(stage) {
    return this.checkState(stage, ExperimentStates.STARTING) || this.checkState(stage, ExperimentStates.RUNNING);
  }
  canEdit(stage, progressionGates) {
    return !gatingUtils.hasWaitingGates(stage.gates) && !gatingUtils.hasWaitingGates(progressionGates);
  }
  checkState(stage, state) {
    if (!stage || !stage.state || !stage.state.State) {
      return false;
    }
    return stage.state.State === state;
  }
}
const ProgressionStageStateUtilsInstance = new ProgressionStageStateUtils();
export {
  ProgressionStageStateUtils,
  ProgressionStageStateUtilsInstance,
  getAggregatedProgressionState,
  getAggregatedStageState,
  getAggregatedState,
  getFriendlyLabel,
  getFrontierStageIndex,
  getFrontierStageIndexFromStates,
  isProgressionNotRunning,
  isProgressionNotStarted,
  isProgressionRunning,
  isProgressionRunningOrStopping,
  isProgressionStageNotRunning,
  isProgressionStageRunningAt100Percent,
  isProgressionStageRunningInLastStep,
  isProgressionStageStateNotRunning,
  isProgressionStateNotRunning,
  showProgressionOnLoad
};
//# sourceMappingURL=ExperimentStateUtils.js.map
